import React, { useEffect, useRef, useState } from 'react';
import TripIssue from './tripIssue';
import TripFlightStop from './tripFlightStop';
import TripFlightConnection from './tripFlightConnection';
import TripIssuesWidget from './tripIssuesWidget';
import ContactInfoWidget from '../messages/contactInfoWidget';
import SentAlertsWidget from '../messages/sentAlertsWidget';
import OptInInvitationWidget from '../messages/optInInvitationWidget';
import ItineraryWidget from './itineraryWidget';
import {
  canManageIssues,
  collectGarbage,
  setHelpPage,
  setWindowTitle,
} from '../../js/configTdm';
import {
  arrivalAirportCityState,
  connectionsStr,
  departureAirportCityState,
  passengerDisplayName,
} from '../../js/lib/filters';
import { tripByTripIdService } from '../../js/trips/tripsServices';
import { Informational } from '../../js/lib/ModalService';
import _ from 'lodash';
import { useParams } from 'react-router-dom';
import TripWaivers from '../../js/waivers/TripWaivers';
import WaiverBulletin from '../../js/waivers/WaiverBulletin';
import {
  buildContactsMaps,
  createTripNotificationUrl,
  filterAlertType,
  filterArrivalAirportCodeCityStateCountry,
  filterDayOfWeek,
  filterDepAirportCodeCityStateCountry,
  filterFlight,
  filterFlightPath,
  filterFlightTimeLabelStr,
  filterFlightTimeValue,
  filterFormatFullDateTime,
  filterSpacedDMY,
  filterStatusIndicator,
  getArrowClass,
  getGoToEditItineraryUrl,
  goToFlightHistoryUrl,
  hopHasIssues,
  legHasFewIssues,
  legHasIssues,
  legHasManyIssues,
  setFirstAndLastFlightDates,
  tripHasIssues,
} from '../../js/lib/trip';
import { InformationalProps, Issue, Trip } from '../../js/headersegment/interfaces';

const TripPage: React.FC = () => {
  const routeParams = useParams<{ tripId: string }>();
  const [widgetIssues, setWidgetIssues] = useState<any[]>([]);
  const [widgetTravelerMsgs, setWidgetTravelerMsgs] = useState<any[]>([]);
  const [widgetFiredAlerts, setWidgetFiredAlerts] = useState<any[]>([]);
  const [widgetTravelerInvitations, setWidgetTravelerInvitations] = useState<
    any[]
  >([]);
  const [contactsMap, setContactsMap] = useState<{ [key: string]: any }>({});
  const [awayContactPointsMap, setAwayContactPointsMap] = useState<{
    [key: string]: any[];
  }>({});
  const [hasWaivers, setHasWaivers] = useState(false);
  const [trip, setTrip] = useState<Trip | any>(null);
  const [legHover, setLegHover] = useState(false);
  const [depHover, setDepHover] = useState<{ [key: string]: boolean }>({});
  const [arrHover, setArrHover] = useState<{ [key: string]: boolean }>({});
  const [
    depFlightTimesAccordionOpen,
    setDepFlightTimesAccordionOpen,
  ] = useState<{ [key: string]: boolean }>({});
  const ref = useRef<HTMLDivElement>(null);
  const tripPanelTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isToggled, setIsToggled] = useState(false);
  const [
    arrFlightTimesAccordionOpen,
    setArrFlightTimesAccordionOpen,
  ] = useState<{ [key: string]: boolean }>({});
  const [informational, setInformational] = useState<InformationalProps | {}>(
    {},
  );

  useEffect(() => {
    if (ref.current) {
      ref.current.style.maxHeight = `${ref.current.scrollHeight}px`;
    }
  }, [ref, depFlightTimesAccordionOpen]);

  useEffect(() => {
    if (ref.current) {
      ref.current.style.maxHeight = `${ref.current.scrollHeight}px`;
    }
  }, [ref, arrFlightTimesAccordionOpen]);

  const includeAlertMessages = true;
  const includeTravelerInvitations = true;

  const awayContactPointAvailable = (travelerKey: string): boolean => {
    return !!(travelerKey && awayContactPointsMap?.[travelerKey]?.length > 0);
  };

  const processAlertMessages = (tripId: string, alertMessages: any[]) => {
    const processedAlerts = (alertMessages || []).map(alertMessage => {
      const dateSentFormatted = filterFormatFullDateTime(
        alertMessage.timestamp,
      );
      const alertType = filterAlertType(alertMessage.alertType);
      const flight = filterFlight(
        alertMessage.airlineCode,
        alertMessage.flightNumber,
      );
      const flightPath = filterFlightPath(
        alertMessage.departureAirportCode,
        alertMessage.arrivalAirportCode,
      );

      const tripNotificationUrl = createTripNotificationUrl(
        tripId,
        alertMessage.notificationId,
      );

      return {
        alertType,
        flight,
        flightPath,
        sentAt: dateSentFormatted,
        tripNotificationUrl,
      };
    });

    setWidgetFiredAlerts(prevAlerts => [...prevAlerts, ...processedAlerts]);
  };

  const processTravelerInvitations = (
    tripId: string,
    travelerInvitations: any[],
  ) => {
    const processedInvitations = (travelerInvitations || []).map(
      travelerInvitation => {
        const dateSentFormatted = filterFormatFullDateTime(
          travelerInvitation.createdAt,
        );
        const email = travelerInvitation.emailAddress;

        const tripNotificationUrl = createTripNotificationUrl(
          tripId,
          travelerInvitation.notificationId,
        );

        return {
          sentAt: dateSentFormatted,
          email,
          tripNotificationUrl,
        };
      },
    );

    setWidgetTravelerInvitations(prevInvitations => [
      ...prevInvitations,
      ...processedInvitations,
    ]);
  };

  const processTrip = (trip: Trip) => {
    if (!trip) {
      throw new Error('Invalid trip arg');
    }

    if (trip.messages) {
      const updatedMessages = trip.messages.map((message: any) => ({
        ...message,
        dateSentFormatted: filterFormatFullDateTime(message.dateSent),
      }));
      setWidgetTravelerMsgs(prev => [...prev, ...updatedMessages]);
    }

    if (trip.tripIssues) {
      const updatedIssues = trip.tripIssues.map(issue => ({
        ...issue,
        leg: 'N/A',
      }));
      setWidgetIssues(prev => [...prev, ...updatedIssues]);
    }

    if (trip.legs) {
      trip.legs.forEach((leg, legIndex) => {
        if (leg.flights) {
          leg.flights.forEach(flight => {
            if (flight.waiverMatches && flight.waiverMatches.length > 0) {
              setHasWaivers(true);
            }

            if (flight.hops) {
              flight.hops.forEach((hop, hopIndex) => {
                hop.airline.name = hop.airline.name || `(${hop.airline.code})`;

                hop.departureTimeSpacedDMY = filterSpacedDMY(
                  hop.departureTime || hop.scheduledGateDepartureDateTime || '',
                );
                hop.departureTimeDayOfWeek = filterDayOfWeek(
                  hop.departureTime || hop.scheduledGateDepartureDateTime || '',
                );
                hop.statusMessageUpperCased = hop.statusMessage?.toUpperCase();
                hop.statusIndicatorStyle = filterStatusIndicator(
                  hop.statusIndicator || '',
                );
                hop.departureAirportCodeCityStateCountry = filterDepAirportCodeCityStateCountry(
                  hop,
                );
                hop.arrivalAirportCodeCityStateCountry = filterArrivalAirportCodeCityStateCountry(
                  hop,
                );

                hop.departureHeadingFlightTimeLabelStr = filterFlightTimeLabelStr(
                  hop,
                  'dep',
                  'heading',
                );
                hop.departureHeadingFlightTimeValue = filterFlightTimeValue(
                  hop,
                  'dep',
                  'heading',
                );
                hop.departurePnrFlightTimeLabelStr = filterFlightTimeLabelStr(
                  hop,
                  'dep',
                  'pnr',
                );
                hop.departurePnrFlightTimeValue = filterFlightTimeValue(
                  hop,
                  'dep',
                  'pnr',
                );
                hop.departureSchGateFlightTimeLabelStr = filterFlightTimeLabelStr(
                  hop,
                  'dep',
                  'schGate',
                );
                hop.departureSchGateFlightTimeValue = filterFlightTimeValue(
                  hop,
                  'dep',
                  'schGate',
                );
                hop.departureActEstGateFlightTimeLabelStr = filterFlightTimeLabelStr(
                  hop,
                  'dep',
                  'actEstGate',
                );
                hop.departureActEstGateFlightTimeValue = filterFlightTimeValue(
                  hop,
                  'dep',
                  'actEstGate',
                );
                hop.departureActEstRunwayFlightTimeLabelStr = filterFlightTimeLabelStr(
                  hop,
                  'dep',
                  'actEstRunway',
                );
                hop.departureActEstRunwayFlightTimeValue = filterFlightTimeValue(
                  hop,
                  'dep',
                  'actEstRunway',
                );

                hop.arrivalHeadingFlightTimeLabelStr = filterFlightTimeLabelStr(
                  hop,
                  'arr',
                  'heading',
                );
                hop.arrivalHeadingFlightTimeValue = filterFlightTimeValue(
                  hop,
                  'arr',
                  'heading',
                );
                hop.arrivalPnrFlightTimeLabelStr = filterFlightTimeLabelStr(
                  hop,
                  'arr',
                  'pnr',
                );
                hop.arrivalPnrFlightTimeValue = filterFlightTimeValue(
                  hop,
                  'arr',
                  'pnr',
                );
                hop.arrivalSchGateFlightTimeLabelStr = filterFlightTimeLabelStr(
                  hop,
                  'arr',
                  'schGate',
                );
                hop.arrivalSchGateFlightTimeValue = filterFlightTimeValue(
                  hop,
                  'arr',
                  'schGate',
                );
                hop.arrivalActEstGateFlightTimeLabelStr = filterFlightTimeLabelStr(
                  hop,
                  'arr',
                  'actEstGate',
                );
                hop.arrivalActEstGateFlightTimeValue = filterFlightTimeValue(
                  hop,
                  'arr',
                  'actEstGate',
                );
                hop.arrivalActEstRunwayFlightTimeLabelStr = filterFlightTimeLabelStr(
                  hop,
                  'arr',
                  'actEstRunway',
                );
                hop.arrivalActEstRunwayFlightTimeValue = filterFlightTimeValue(
                  hop,
                  'arr',
                  'actEstRunway',
                );

                if (hop.hopIssues) {
                  const updatedHopIssues = hop.hopIssues.map(issue => ({
                    ...issue,
                    airline: hop.airline,
                    number: hop.number,
                    status: hop.statusMessage,
                    leg: legIndex + 1,
                  }));
                  setWidgetIssues(prev => [...prev, ...updatedHopIssues]);
                }

                if (hop.connectionIssues) {
                  const updatedConnectionIssues = hop.connectionIssues.map(
                    issue => ({
                      ...issue,
                      airline: hop.airline,
                      number: hop.number,
                      status: hop.statusMessage,
                      leg: legIndex + 1,
                    }),
                  );
                  setWidgetIssues(prev => [
                    ...prev,
                    ...updatedConnectionIssues,
                  ]);
                }
              });
            }
          });
        }
      });
    }
    buildContactsMaps(trip, setContactsMap, setAwayContactPointsMap);
    setFirstAndLastFlightDates(trip, setTrip);
  };

  useEffect(() => {
    setWindowTitle('Trip Details');
    setHelpPage('helpTrip');
    tripByTripIdService()
      .fetch(
        routeParams.tripId!,
        includeAlertMessages,
        includeTravelerInvitations,
      )
      .then(resp => {
        if (resp?.status === 200) {
          if (resp?.data?.recordLocator) {
            processTrip(resp.data);
            const alertMessages = resp.data.alertMessages;
            processAlertMessages(routeParams.tripId!, alertMessages);

            const travelerInvitations = resp.data.travelerInvitations;
            processTravelerInvitations(routeParams.tripId!, travelerInvitations);
          } else {
            setInformational({
              title: 'Trip Details Not Found',
              message1:
                'This may have occurred because trip details are not available in our system beyond two years from the last travel date in the trip.',
              useCancelButton: false,
              flag: 'informational',
            });
          }
        }
      });
    return () => {
      setWidgetIssues([]);
      setWidgetTravelerMsgs([]);
      setContactsMap({});
      setAwayContactPointsMap({});
      setTrip(null);
      collectGarbage();
    };
  }, []);

  useEffect(() => {
    if (isToggled && ref.current) {
      ref.current.style.maxHeight = 'fit-content';
      setIsToggled(false);
    }
  }, [isToggled]);

  if ((informational as InformationalProps).flag === 'informational') {
    return (
      <Informational
        {...(informational as InformationalProps)}
        onSubmit={() => {
          window.location.href = '/trips/search';
          setInformational({});
        }}
        cancel={() => setInformational({})}
      ></Informational>
    );
  }

  return (
    <>
      {trip && (
        <div className="row trip">
          <div className="col-md-9">
            {tripHasIssues(trip) && (
              <div className="row flight-legs">
                <div className="col-md-12 flight-leg">
                  {trip.tripIssues &&
                    trip.tripIssues.map((issue: Issue, index: number) => {
                      return (
                        <div key={index}>
                          <div>
                            <TripIssue
                              trip={trip}
                              awayContactPointsMap={awayContactPointsMap}
                              parentIssue={issue}
                            ></TripIssue>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
            <div className="row header">
              {canManageIssues() &&
                trip.firstDepartureDate &&
                trip.lastArrivalDate ? (
                <div className="col-md-4 rec-loc">
                  TRIP&nbsp;-&nbsp;
                  <a
                    title="Click to Edit Itinerary"
                    href={getGoToEditItineraryUrl(trip)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {trip.recordLocator}
                  </a>
                </div>
              ) : (
                <div className="col-md-4 rec-loc">
                  TRIP&nbsp;-&nbsp;{trip.recordLocator}
                </div>
              )}
              <div className="col-md-8 secondary">
                <div className="col-md-12">
                  <div className="col-md-4 lg-label lg-label-hxs hidden-sm hidden-xs">
                    PASSENGER(S)
                  </div>
                  <div className="col-md-4 lg-label lg-label-xs visible-sm visible-xs">
                    PASSENGER(S)
                  </div>
                  <div className="col-md-8 lg-text passengers-groups">
                    {trip.passengers.map((passenger: any, index: number) => (
                      <span key={index}>
                        {passengerDisplayName(passenger)}
                        {index < trip.passengers.length - 1 && ', '}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="col-md-4 lg-label lg-label-hxs hidden-sm hidden-xs">
                    GROUP
                  </div>
                  <div className="col-md-4 lg-label lg-label-xs visible-sm visible-xs">
                    GROUP
                  </div>
                  <div className="col-md-8 lg-text passengers-groups">
                    {trip.groupName}
                  </div>
                </div>
              </div>
            </div>
            <div className="row flight-legs">
              <div className="col-md-12 flight-leg">
                <div data-uib-accordion>
                  <div className="panel-group">
                    {trip.legs.map((leg: any, index: number) => (
                      <div
                        key={index}
                        id={`leg-${index + 1}`}
                        className={`panel ${legHasIssues(leg)
                          ? 'leg-has-issues'
                          : 'leg-has-no-issues'
                          }`}
                      >
                        <div className="panel-heading">
                          <div className="panel-title">
                            <a>
                              <div
                                className="text-align-center"
                                onClick={() => {
                                  let updateLegs = trip.legs.map(
                                    (line: any, legIndex: number) => {
                                      if (legIndex === index) {
                                        if (line.hasIssues && ref.current) {
                                          if (tripPanelTimeoutRef.current) {
                                            clearTimeout(tripPanelTimeoutRef.current); // Clear any pending timeouts
                                          }
                                          ref.current.style.maxHeight = `${ref.current.scrollHeight}px`;
                                          tripPanelTimeoutRef.current = setTimeout(() => {
                                            if (ref.current) ref.current.style.maxHeight = '0px';
                                          }, 200);
                                        } else if (!line.hasIssues && ref.current) {
                                          ref.current.style.maxHeight = `${ref.current.scrollHeight}px`;
                                        }
                                        return { ...line, hasIssues: !line.hasIssues };
                                      }
                                      return line;
                                    },
                                  );
                                  setTrip({ ...trip, legs: updateLegs });
                                }}
                                onMouseEnter={() => setLegHover(true)}
                                onMouseLeave={() => setLegHover(false)}
                              >
                                <i
                                  className={`pull-left sprites ${getArrowClass(
                                    leg,
                                    legHover,
                                  )}`}
                                ></i>
                                <div className="hidden-xs flex-1">
                                  Leg {index + 1} -{' '}
                                  {departureAirportCityState(leg)} to{' '}
                                  {arrivalAirportCityState(leg)}{' '}
                                  {connectionsStr(leg.connections)}
                                </div>
                                <div className="visible-xs flex-1">
                                  Leg {index + 1} - {leg.departureAirportCode}{' '}
                                  to {leg.arrivalAirportCode}
                                </div>
                                {legHasFewIssues(leg) && (
                                  <div
                                    className={`sprites sprite-issues-ivory-${leg.issuesCount}-24`}
                                  ></div>
                                )}
                                {legHasManyIssues(leg) && (
                                  <div className="sprites sprite-issues-ivory-many-24"></div>
                                )}
                              </div>
                            </a>
                          </div>
                        </div>
                        <div ref={ref} className={`panel-collapse leg-issues-panel ` + (leg.hasIssues ? 'in' : '')}>
                          <div className="panel-body">
                            {leg.flights.map((flight: any, legindex: number) => (
                              <div
                                key={legindex}
                                className={legindex !== 0 ? 'separator' : ''}
                              >
                                {flight.waiverMatches.map(
                                  (waiverMatch: any) => (
                                    <div>
                                      <WaiverBulletin
                                        waiver={waiverMatch.waiver}
                                        matchCriteria={
                                          waiverMatch.waiverCriteriaToMatchTypeMap
                                        }
                                      />
                                    </div>
                                  ),
                                )}
                                {flight.hops.map((hop: any, index: number) => (
                                  <div key={index}>
                                    {hopHasIssues(hop) && (
                                      <div>
                                        {hop.hopIssues.map(
                                          (issue: Issue, index: number) => (
                                            <div key={index}>
                                              <div>
                                                <TripIssue
                                                  trip={trip}
                                                  awayContactPointsMap={
                                                    awayContactPointsMap
                                                  }
                                                  parentIssue={issue}
                                                  callback={() => setIsToggled(true)}
                                                ></TripIssue>
                                              </div>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    )}
                                    <div className="row flight-info">
                                      <div className="col-md-12">
                                        <div className="row">
                                          <div className="col-md-12 flight-leg-header">
                                            {hop.airline.code} {hop.number} (
                                            {hop.departureAirportCode} to{' '}
                                            {hop.arrivalAirportCode})
                                          </div>
                                        </div>
                                        <div className="row">
                                          <div className="col-md-6">
                                            <div className="row">
                                              <div className="col-md-4 fs-label fs-label-hxs hidden-sm hidden-xs">
                                                FLIGHT
                                              </div>
                                              <div className="col-md-4 fs-label fs-label-xs visible-sm visible-xs">
                                                FLIGHT
                                              </div>
                                              <div className="col-md-8 fs-text">
                                                {canManageIssues() &&
                                                  hop.flightHistoryId ? (
                                                  <span className="flight-number">
                                                    <a
                                                      onClick={(event: any) => {
                                                        event.stopPropagation();
                                                        goToFlightHistoryUrl(
                                                          hop,
                                                        )
                                                      }}
                                                    >
                                                      {hop.airline.name}{' '}
                                                      {hop.number}
                                                    </a>
                                                  </span>
                                                ) : (
                                                  <span className="flight-number">
                                                    {hop.airline.name}{' '}
                                                    {hop.number}
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                            <div className="row">
                                              <div className="col-md-4 fs-label fs-label-hxs hidden-sm hidden-xs">
                                                DATE
                                              </div>
                                              <div className="col-md-4 fs-label fs-label-xs visible-sm visible-xs">
                                                DATE
                                              </div>
                                              <div className="col-md-8 fs-text">
                                                <span className="weighted-value">
                                                  {hop.departureTimeSpacedDMY}
                                                </span>
                                                <span>
                                                  ({hop.departureTimeDayOfWeek})
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="col-md-6">
                                            <div className="hop-status-box-hxs hop-status-box hidden-sm hidden-xs">
                                              <div
                                                className={
                                                  hop.statusIndicatorStyle
                                                }
                                              >
                                                {hop.statusMessageUpperCased}
                                              </div>
                                            </div>
                                            <div className="hop-status-box visible-sm visible-xs">
                                              <div
                                                className={
                                                  hop.statusIndicatorStyle
                                                }
                                              >
                                                {hop.statusMessageUpperCased}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="row separator"></div>
                                        <div className="row">
                                          <div className="col-md-6">
                                            <div className="row">
                                              <div className="col-md-4 fs-label fs-label-hxs hidden-sm hidden-xs">
                                                DEPARTURE
                                              </div>
                                              <div className="col-md-4 fs-label fs-label-xs visible-sm visible-xs">
                                                DEPARTURE
                                              </div>
                                              <div className="col-md-8 fs-text">
                                                <span>
                                                  {
                                                    hop.departureAirportCodeCityStateCountry
                                                  }
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="col-md-6 flight-times hidden-sm hidden-xs">
                                            <div className="row">
                                              <div
                                                className="col-md-1 plus-minus"
                                                onMouseEnter={() =>
                                                  setDepHover({...depHover, [legindex]: true})
                                                }
                                                onMouseLeave={() =>
                                                  setDepHover({...depHover, [legindex]: false})
                                                }
                                              >
                                                <i
                                                  onClick={() => {
                                                    setDepFlightTimesAccordionOpen(
                                                      {...depFlightTimesAccordionOpen,[legindex]: depFlightTimesAccordionOpen[legindex] ? !depFlightTimesAccordionOpen[legindex] : true},
                                                    );
                                                  }
                                                  }
                                                  className={
                                                    'pull-left sprites ' +
                                                    (depFlightTimesAccordionOpen[legindex]
                                                      ? depHover[legindex]
                                                        ? 'sprite-minus-orange'
                                                        : 'sprite-minus'
                                                      : depHover[legindex]
                                                        ? 'sprite-plus-orange'
                                                        : 'sprite-plus')
                                                  }
                                                ></i>
                                              </div>
                                              <div className="col-md-11 desc-list">
                                                <dl>
                                                  <dt>
                                                    {
                                                      hop.departureHeadingFlightTimeLabelStr
                                                    }
                                                  </dt>
                                                  <dd className="weighted-value">
                                                    {
                                                      hop.departureHeadingFlightTimeValue
                                                    }
                                                  </dd>
                                                </dl>
                                              </div>
                                            </div>
                                            {depFlightTimesAccordionOpen[legindex] && (
                                              <div>
                                                <div className="accordion-heading"></div>
                                                <div className="row">
                                                  <div className="col-md-1 plus-minus"></div>
                                                  <div className="col-md-11 desc-list">
                                                    <dl>
                                                      <dt>
                                                        {
                                                          hop.departurePnrFlightTimeLabelStr
                                                        }
                                                      </dt>
                                                      <dd>
                                                        {
                                                          hop.departurePnrFlightTimeValue
                                                        }
                                                      </dd>
                                                      <dt>
                                                        {
                                                          hop.departureSchGateFlightTimeLabelStr
                                                        }
                                                      </dt>
                                                      <dd>
                                                        {
                                                          hop.departureSchGateFlightTimeValue
                                                        }
                                                      </dd>
                                                      <dt>
                                                        {
                                                          hop.departureActEstGateFlightTimeLabelStr
                                                        }
                                                      </dt>
                                                      <dd>
                                                        {
                                                          hop.departureActEstGateFlightTimeValue
                                                        }
                                                      </dd>
                                                      <dt>
                                                        {
                                                          hop.departureActEstRunwayFlightTimeLabelStr
                                                        }
                                                      </dt>
                                                      <dd>
                                                        {
                                                          hop.departureActEstRunwayFlightTimeValue
                                                        }
                                                      </dd>
                                                    </dl>
                                                  </div>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                          <div className="col-md-6 visible-sm visible-xs">
                                            <div className="row">
                                              <div className="col-md-4 fs-label fs-label-xs visible-xs">
                                                {
                                                  hop.departureHeadingFlightTimeLabelStr
                                                }
                                              </div>
                                              <div className="col-md-8 fs-text">
                                                <span className="weighted-value">
                                                  {
                                                    hop.departureHeadingFlightTimeValue
                                                  }
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="row">
                                          <div className="col-md-6">
                                            <div className="row">
                                              <div className="col-md-4 fs-label fs-label-hxs hidden-sm hidden-xs">
                                                ARRIVAL
                                              </div>
                                              <div className="col-md-4 fs-label fs-label-xs visible-sm visible-xs">
                                                ARRIVAL
                                              </div>
                                              <div className="col-md-8 fs-text">
                                                <span>
                                                  {
                                                    hop.arrivalAirportCodeCityStateCountry
                                                  }
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="col-md-6 flight-times hidden-sm hidden-xs">
                                            <div className="row">
                                              <div
                                                className="col-md-1 plus-minus"
                                                onMouseEnter={() =>
                                                  setArrHover({...arrHover, [legindex]: true})
                                                }
                                                onMouseLeave={() =>
                                                  setArrHover({...arrHover, [legindex]: false})
                                                }
                                              >
                                                <i
                                                  onClick={() => {
                                                      setArrFlightTimesAccordionOpen(
                                                        {...arrFlightTimesAccordionOpen, [legindex]: arrFlightTimesAccordionOpen[legindex] ? !arrFlightTimesAccordionOpen[legindex] : true},
                                                      );
                                                    }
                                                  }
                                                  className={
                                                    'pull-left sprites ' +
                                                    (arrFlightTimesAccordionOpen[legindex]
                                                      ? arrHover[legindex]
                                                        ? 'sprite-minus-orange'
                                                        : 'sprite-minus'
                                                      : arrHover[legindex]
                                                        ? 'sprite-plus-orange'
                                                        : 'sprite-plus')
                                                  }
                                                ></i>
                                              </div>
                                              <div className="col-md-11 desc-list">
                                                <dl>
                                                  <dt>
                                                    {
                                                      hop.arrivalHeadingFlightTimeLabelStr
                                                    }
                                                  </dt>
                                                  <dd className="weighted-value">
                                                    {
                                                      hop.arrivalHeadingFlightTimeValue
                                                    }
                                                  </dd>
                                                </dl>
                                              </div>
                                            </div>

                                            {arrFlightTimesAccordionOpen[legindex] && (
                                              <div>
                                                <div className="accordion-heading"></div>
                                                <div className="row">
                                                  <div className="col-md-1 plus-minus"></div>
                                                  <div className="col-md-11 desc-list">
                                                    <dl>
                                                      <dt>
                                                        {
                                                          hop.arrivalPnrFlightTimeLabelStr
                                                        }
                                                      </dt>
                                                      <dd>
                                                        {
                                                          hop.arrivalPnrFlightTimeValue
                                                        }
                                                      </dd>
                                                      <dt>
                                                        {
                                                          hop.arrivalSchGateFlightTimeLabelStr
                                                        }
                                                      </dt>
                                                      <dd>
                                                        {
                                                          hop.arrivalSchGateFlightTimeValue
                                                        }
                                                      </dd>
                                                      <dt>
                                                        {
                                                          hop.arrivalActEstGateFlightTimeLabelStr
                                                        }
                                                      </dt>
                                                      <dd>
                                                        {
                                                          hop.arrivalActEstGateFlightTimeValue
                                                        }
                                                      </dd>
                                                      <dt>
                                                        {
                                                          hop.arrivalActEstRunwayFlightTimeLabelStr
                                                        }
                                                      </dt>
                                                      <dd>
                                                        {
                                                          hop.arrivalActEstRunwayFlightTimeValue
                                                        }
                                                      </dd>
                                                    </dl>
                                                  </div>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                          <div className="col-md-6 visible-sm visible-xs">
                                            <div className="row">
                                              <div className="col-md-4 fs-label fs-label-xs visible-xs">
                                                {
                                                  hop.arrivalHeadingFlightTimeLabelStr
                                                }
                                              </div>
                                              <div className="col-md-8 fs-text">
                                                <span className="weighted-value">
                                                  {
                                                    hop.arrivalHeadingFlightTimeValue
                                                  }
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    {hop && hop.stop != null && (
                                      <div>
                                         <TripFlightStop hop={hop} />
                                      </div>
                                    )}
                                    {hop && hop.connection != null && (
                                      <div>
                                        <div>
                                          <TripFlightConnection
                                            trip={trip}
                                            awayContactPointsMap={
                                              awayContactPointsMap
                                            }
                                            hop={hop}
                                          ></TripFlightConnection>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3 widgets">
            {hasWaivers && <TripWaivers trip={trip}></TripWaivers>}
            {canManageIssues() && (
              <div className="hidden-sm hidden-xs">
                <TripIssuesWidget widgetIssues={widgetIssues} />
              </div>
            )}
            <div>
              <ContactInfoWidget
                contactsMap={contactsMap}
                canManageIssues={canManageIssues}
                awayContactPointAvailable={awayContactPointAvailable}
              />
            </div>
            <div>
              <SentAlertsWidget widgetFiredAlerts={widgetFiredAlerts} />
            </div>
            <div>
              <OptInInvitationWidget
                widgetTravelerInvitations={widgetTravelerInvitations}
              />
            </div>
            <div className="hidden-sm hidden-xs">
              <ItineraryWidget trip={trip} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TripPage;
